import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { useSessionExpiry } from './hooks/useSessionExpiry';
import { useTokenLifecycle } from './hooks/useTokenLifecycle';
import { useAppDispatch, useAppSelector } from './store';
import { getCurrentUser } from './store/slices/authSlice';
import { storage } from './utils/storage';
import { getTokenExpiryMs } from './utils/jwt';
import { performSessionLogout, type SessionLogoutReason } from './utils/sessionManager';
import { UserRole } from './types';
import AuthenticatedHomeRedirect from './components/auth/AuthenticatedHomeRedirect';
import PlanFeatureGuard from './components/auth/PlanFeatureGuard';
import EconomicsGuard from './components/auth/EconomicsGuard';
import RouteAccessDenied from './components/auth/RouteAccessDenied';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import IntegrationCallback from './pages/IntegrationCallback';
import Dashboard from './pages/Dashboard';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import Billing from './pages/Billing';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';
import JoinWaitlist from './pages/JoinWaitlist';
import { ThemeProvider } from './contexts/ThemeContext';
import I18nSync from './components/I18nSync';
import PageMeta from './components/PageMeta';
import { HierarchyProvider } from './contexts/HierarchyContext';

const Projects = lazy(() => import('./pages/Projects'));
const Board = lazy(() => import('./pages/Board'));
const ListView = lazy(() => import('./pages/ListView'));
const Backlog = lazy(() => import('./pages/Backlog'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Reports = lazy(() => import('./pages/Reports'));
const TimeTracking = lazy(() => import('./pages/TimeTracking'));
const SprintMaster = lazy(() => import('./pages/SprintMaster'));
const AIStudio = lazy(() => import('./pages/AIStudio'));
const AIWhiteboard = lazy(() => import('./pages/AIWhiteboard'));
const AIAssistant = lazy(() => import('./components/AIAssistant'));
const StakeholderView = lazy(() => import('./pages/StakeholderView'));
const ProjectEconomics = lazy(() => import('./pages/ProjectEconomics'));
const Members = lazy(() => import('./pages/Members'));
const Settings = lazy(() => import('./pages/Settings'));
const Notifications = lazy(() => import('./pages/Notifications'));
const GanttChart = lazy(() => import('./pages/GanttChart'));
const Workload = lazy(() => import('./pages/Workload'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const SSOConfig = lazy(() => import('./pages/SSOConfig'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Webhooks = lazy(() => import('./pages/Webhooks'));
const WebhookDetail = lazy(() => import('./pages/WebhookDetail'));
const Workflows = lazy(() => import('./pages/Workflows'));
const WorkflowBuilder = lazy(() => import('./pages/WorkflowBuilder'));
const WorkflowDetail = lazy(() => import('./pages/WorkflowDetail'));
const Roles = lazy(() => import('./pages/Roles'));
const RoleBuilder = lazy(() => import('./pages/RoleBuilder'));
const RoleDetail = lazy(() => import('./pages/RoleDetail'));
const RolePresets = lazy(() => import('./pages/RolePresets'));
const TaskPermalink = lazy(() => import('./pages/TaskPermalink'));
const Milestones = lazy(() => import('./pages/Milestones'));
const ProjectWiki = lazy(() => import('./pages/ProjectWiki'));
const OrgSettings = lazy(() => import('./pages/OrgSettings'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const CostDashboard = lazy(() => import('./pages/CostDashboard'));
const WorkspaceHub = lazy(() => import('./pages/WorkspaceHub'));
const OrgSetup = lazy(() => import('./pages/org/OrgSetup'));
const OrgProducts = lazy(() => import('./pages/org/OrgProducts'));
const LicensesUsers = lazy(() => import('./pages/admin/LicensesUsers'));
const OrgHome = lazy(() => import('@/products/org/pages/OrgHome'));
const OrgSecurity = lazy(() => import('@/products/org/pages/OrgSecurity'));
const AwaitingProductAccess = lazy(() => import('./pages/AwaitingProductAccess'));
const SatelliteProductShell = lazy(() => import('./pages/SatelliteProductShell'));
const CodeLayout = lazy(() => import('./pages/code/CodeLayout'));
const CodeHome = lazy(() => import('./pages/code/CodeHome'));
import PmPathRedirect from './components/routing/PmPathRedirect';

const RouteLoader = () => (
  <div className="min-h-[40vh] flex items-center justify-center bg-gray-50">
  </div>
);

const LazyRoute = ({ children }: { children: JSX.Element }) => (
  <Suspense fallback={<RouteLoader />}>
    {children}
  </Suspense>
);

/** `/wiki` is legacy; handbook UI lives at `/handbook`. */
function RedirectWikiToHandbook() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/handbook', search, hash }} replace />;
}

/**
 * Auth Initializer Component
 * Validates stored token on app load
 */
/**
 * Decode JWT exp claim without a library. Returns expiry ms or null.
 */
function getTokenExpiry(token: string): number | null {
  return getTokenExpiryMs(token);
}

const AuthInitializer = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  useSessionExpiry();
  useTokenLifecycle();

  useEffect(() => {
    const onForcedLogout = (event: Event) => {
      const reason = (event as CustomEvent<{ reason: SessionLogoutReason }>).detail?.reason || 'expired';
      void performSessionLogout(dispatch, reason);
    };
    window.addEventListener('cotaskr:session-logout', onForcedLogout);
    return () => window.removeEventListener('cotaskr:session-logout', onForcedLogout);
  }, [dispatch]);

  useEffect(() => {
    const initAuth = async () => {
      const token = storage.getAccessToken();

      if (!token) return;

      const expiry = getTokenExpiry(token);
      if (expiry !== null && expiry <= Date.now()) {
        await performSessionLogout(dispatch, 'startup', { showToast: false });
        return;
      }

      // Token is valid — fetch user profile if not yet loaded
      if (!user) {
        try {
          await dispatch(getCurrentUser()).unwrap();
        } catch {
          // 401 will be caught by the RTK Query interceptor which dispatches logout()
        }
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
};

/** Legacy notification links used `/tasks/:id` — forward to canonical `/task/:ref`. */
function LegacyTaskRedirect() {
  const { ref } = useParams<{ ref: string }>();
  return <Navigate to={ref ? `/task/${ref}` : '/dashboard'} replace />;
}

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 */
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

/**
 * Role Guard Component
 * Redirects to the user's home when they lack permission for a route
 */
const RoleGuard = ({ roles, children }: { roles: UserRole[]; children: JSX.Element }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const userRole = user?.role as UserRole | undefined;

  if (!userRole || !roles.includes(userRole)) {
    return (
      <RouteAccessDenied
        title={t('common.accessRestricted')}
        message={t('common.accessRestrictedMsg')}
      />
    );
  }

  return children;
};

/** Default landing — license-aware (admins → /org, members → Plan or awaiting-access). */
function HomeRedirect() {
  return <AuthenticatedHomeRedirect />;
}

/**
 * Main Application Component
 * Configures routing and layout structure
 */
function App() {
  const { isAuthenticated } = useAuth();

  return (
    <ThemeProvider>
    <I18nSync />
    <PageMeta />
    <AuthInitializer>
      <HierarchyProvider>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <AuthenticatedHomeRedirect /> : <Login />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <AuthenticatedHomeRedirect /> : <Register />}
        />
        <Route
          path="/forgot-password"
          element={isAuthenticated ? <AuthenticatedHomeRedirect /> : <ForgotPassword />}
        />
        <Route
          path="/reset-password"
          element={isAuthenticated ? <AuthenticatedHomeRedirect /> : <ResetPassword />}
        />
        <Route
          path="/accept-invite"
          element={isAuthenticated ? <AuthenticatedHomeRedirect /> : <AcceptInvite />}
        />
        <Route
          path="/join-waitlist"
          element={isAuthenticated ? <AuthenticatedHomeRedirect /> : <JoinWaitlist />}
        />
        <Route
          path="/auth/sso-callback"
          element={<SSOCallback />}
        />
        <Route
          path="/integrations/callback"
          element={<IntegrationCallback />}
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomeRedirect />} />
          <Route
            path="org/setup"
            element={
              <LazyRoute>
                <RoleGuard roles={[UserRole.ADMIN]}>
                  <OrgSetup />
                </RoleGuard>
              </LazyRoute>
            }
          />
          <Route
            path="org"
            element={
              <LazyRoute>
                <RoleGuard roles={[UserRole.ADMIN]}>
                  <OrgHome />
                </RoleGuard>
              </LazyRoute>
            }
          />
          <Route
            path="org/products"
            element={
              <LazyRoute>
                <RoleGuard roles={[UserRole.ADMIN]}>
                  <OrgProducts />
                </RoleGuard>
              </LazyRoute>
            }
          />
          <Route
            path="org/licenses"
            element={
              <LazyRoute>
                <RoleGuard roles={[UserRole.ADMIN]}>
                  <LicensesUsers />
                </RoleGuard>
              </LazyRoute>
            }
          />
          <Route
            path="org/security"
            element={
              <LazyRoute>
                <RoleGuard roles={[UserRole.ADMIN]}>
                  <OrgSecurity />
                </RoleGuard>
              </LazyRoute>
            }
          />
          <Route
            path="awaiting-access"
            element={
              <LazyRoute>
                <AwaitingProductAccess />
              </LazyRoute>
            }
          />
          <Route path="workspace" element={<LazyRoute><WorkspaceHub /></LazyRoute>} />
          <Route path="pm/*" element={<PmPathRedirect />} />
          <Route path="code/*" element={<LazyRoute><CodeLayout /></LazyRoute>}>
            <Route index element={<CodeHome />} />
          </Route>
          <Route path="ship/*" element={<LazyRoute><SatelliteProductShell productId="ship" /></LazyRoute>} />
          <Route path="lenc/*" element={<LazyRoute><SatelliteProductShell productId="lenc" /></LazyRoute>} />
          <Route path="my-week" element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="projects" element={<LazyRoute><Projects /></LazyRoute>} />
          <Route path="board" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><Board /></RoleGuard></LazyRoute>} />
          <Route path="board/:projectId" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><Board /></RoleGuard></LazyRoute>} />
          <Route path="list" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><ListView /></RoleGuard></LazyRoute>} />
          <Route path="list/:projectId" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><ListView /></RoleGuard></LazyRoute>} />
          <Route path="backlog" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><Backlog /></RoleGuard></LazyRoute>} />
          <Route path="milestones" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><Milestones /></RoleGuard></LazyRoute>} />
          <Route
            path="handbook"
            element={
              <LazyRoute>
                <RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}>
                  <ProjectWiki />
                </RoleGuard>
              </LazyRoute>
            }
          />
          <Route
            path="wiki"
            element={
              <RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}>
                <RedirectWikiToHandbook />
              </RoleGuard>
            }
          />
          <Route path="calendar" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><Calendar /></RoleGuard></LazyRoute>} />
          <Route path="gantt" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><PlanFeatureGuard feature="advancedReporting"><GanttChart /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="workload" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER]}><PlanFeatureGuard feature="advancedReporting"><Workload /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="reports" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><PlanFeatureGuard feature="advancedReporting"><Reports /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="stakeholder" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.STAKEHOLDER]}><PlanFeatureGuard feature="advancedReporting"><StakeholderView /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="economics" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.STAKEHOLDER]}><EconomicsGuard><ProjectEconomics /></EconomicsGuard></RoleGuard></LazyRoute>} />
          <Route path="cost" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER]}><CostDashboard /></RoleGuard></LazyRoute>} />
          <Route path="ai-studio" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><AIStudio /></RoleGuard></LazyRoute>} />
          <Route path="ai-whiteboard" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><AIWhiteboard /></RoleGuard></LazyRoute>} />
          <Route path="ai-assistant" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><AIAssistant /></RoleGuard></LazyRoute>} />
          <Route path="sprints" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><SprintMaster /></RoleGuard></LazyRoute>} />
          <Route path="time-tracking" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER]}><TimeTracking /></RoleGuard></LazyRoute>} />
          <Route path="members" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><Members /></RoleGuard></LazyRoute>} />
          <Route path="teams" element={<Navigate to="/members" replace />} />
          <Route path="settings" element={<LazyRoute><Settings /></LazyRoute>} />
          <Route path="notifications" element={<LazyRoute><Notifications /></LazyRoute>} />
          <Route path="ai-usage" element={<Navigate to="/admin?tab=consumption" replace />} />
          <Route path="admin" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN]}><AdminPanel /></RoleGuard></LazyRoute>} />
          <Route path="admin/org-settings" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><OrgSettings /></RoleGuard></LazyRoute>} />
          <Route path="admin/audit-logs" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><AuditLogs /></RoleGuard></LazyRoute>} />
          <Route
            path="sso-config"
            element={
              <LazyRoute>
                <RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
                  <PlanFeatureGuard feature="sso">
                    <SSOConfig />
                  </PlanFeatureGuard>
                </RoleGuard>
              </LazyRoute>
            }
          />
          <Route path="integrations" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><PlanFeatureGuard feature="integrations"><Integrations /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="webhooks" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><PlanFeatureGuard feature="apiAccess"><Webhooks /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="webhooks/:id" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><PlanFeatureGuard feature="apiAccess"><WebhookDetail /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="workflows" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><PlanFeatureGuard feature="apiAccess"><Workflows /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="workflows/new" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><PlanFeatureGuard feature="apiAccess"><WorkflowBuilder /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="workflows/:id" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><PlanFeatureGuard feature="apiAccess"><WorkflowDetail /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="workflows/:id/edit" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><PlanFeatureGuard feature="apiAccess"><WorkflowBuilder /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="roles" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN]}><PlanFeatureGuard feature="customRoles"><Roles /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="roles/new" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN]}><PlanFeatureGuard feature="customRoles"><RoleBuilder /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="roles/presets" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN]}><PlanFeatureGuard feature="customRoles"><RolePresets /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="roles/:id" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN]}><PlanFeatureGuard feature="customRoles"><RoleDetail /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="roles/:id/edit" element={<LazyRoute><RoleGuard roles={[UserRole.SUPER_ADMIN]}><PlanFeatureGuard feature="customRoles"><RoleBuilder /></PlanFeatureGuard></RoleGuard></LazyRoute>} />
          <Route path="custom-fields" element={<Navigate to="/admin/org-settings?tab=custom-fields" replace />} />
          <Route path="billing" element={<RoleGuard roles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}><Billing /></RoleGuard>} />
          <Route path="billing/success" element={<BillingSuccess />} />
          <Route path="billing/cancel" element={<BillingCancel />} />
          <Route path="task/:ref" element={<LazyRoute><TaskPermalink typeSlug="task" /></LazyRoute>} />
          <Route path="tasks/:ref" element={<LegacyTaskRedirect />} />
          <Route path="userstory/:ref" element={<LazyRoute><TaskPermalink typeSlug="userstory" /></LazyRoute>} />
          <Route path="epic/:ref" element={<LazyRoute><TaskPermalink typeSlug="epic" /></LazyRoute>} />
          <Route path="feature/:ref" element={<LazyRoute><TaskPermalink typeSlug="feature" /></LazyRoute>} />
          <Route path="bug/:ref" element={<LazyRoute><TaskPermalink typeSlug="bug" /></LazyRoute>} />
        </Route>

        {/* 404 Route */}
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
      </HierarchyProvider>
    </AuthInitializer>
    </ThemeProvider>
  );
}

export default App;
